import type { FurnitureDef, IglooPlacement } from "@clubap/shared";
import type { PointerEvent as ReactPointerEvent } from "react";
import { IconCaja, IconIglu, IconTienda } from "./iconsIgloo.js";
import type { IglooSnapshot } from "../economy/igloo.js";

interface Props {
  snapshot: IglooSnapshot;
  /** Con lo que se paga acá: tus monedas en el iglú, la caja común en un club. */
  balance: number;
  /** Ese saldo es de un club: se rotula, porque gastarlo no es gastar lo tuyo. */
  caja?: boolean;
  /** Se está arrastrando algo ahora mismo: el basurero se enciende. */
  dragging: boolean;
  grid: boolean;
  onToggleGrid: () => void;
  onOpenShop: () => void;
  /** Cambiar el piso y la pared. */
  onOpenStyle: () => void;
  onDone: () => void;
  /** Empieza el arrastre de una pieza NUEVA desde la barra. */
  onPickUp: (furniture: FurnitureDef, event: ReactPointerEvent) => void;
}

/**
 * La barra de decorar: lo que tienes sin poner, y qué hacer con el cuarto.
 *
 * Es una sola fila —fichas a la izquierda, herramientas a la derecha— y no dos como en
 * Club Penguin. La razón es el sitio: su editor tenía el iglú dibujado en el centro con
 * márgenes de sobra, y acá el cuarto ES la pantalla. Dos filas se comían el tercio
 * superior, que es justo donde se coloca contra la pared.
 *
 * Lo que sí se copia, porque es lo que hace que el inventario se lea de un vistazo: la
 * ficha con el CONTADOR encima. Sin él hay que abrir cada cosa para saber cuántas
 * quedan.
 *
 * Acá no se decide nada: los gestos suben a `App`, que es quien tiene el stage y sabe
 * traducir un punto de la pantalla a un punto del mundo.
 */
export function IglooBar({
  snapshot,
  balance,
  caja = false,
  dragging,
  grid,
  onToggleGrid,
  onOpenShop,
  onOpenStyle,
  onDone,
  onPickUp,
}: Props) {
  /** Cuántas quedan sin poner. La misma cuenta que hace el servidor al validar. */
  const disponibles = (furnitureId: string): number => {
    const owned = snapshot.owned[furnitureId] ?? 0;
    const puestas = snapshot.placements.filter(
      (placement: IglooPlacement) => placement.furnitureId === furnitureId,
    ).length;
    return owned - puestas;
  };

  /* Sólo lo que se puede arrastrar. Una ficha en cero es una ficha que no hace nada, y
     el sitio que ocupa lo necesita la que sí. Para conseguir más está la tienda. */
  const enMano = snapshot.furniture.filter((furniture) => disponibles(furniture.id) > 0);
  const lleno = snapshot.placements.length >= snapshot.maxPlaced;

  return (
    <>
      <div className="iglu-barra">
        <div className="iglu-barra__fichas">
          {enMano.length === 0 && (
            <p className="iglu-barra__vacio">
              No te queda nada sin poner. Compra algo en la tienda →
            </p>
          )}

          {enMano.map((furniture) => (
            <button
              className="iglu-ficha"
              type="button"
              key={furniture.id}
              disabled={lleno}
              title={lleno ? "El cuarto ya está lleno" : `${furniture.name} · arrastra al piso`}
              /* `pointerdown` y no `click`: el gesto es arrastrar, y un `click` sólo
                 existe si sueltas donde empezaste — justo lo que acá no pasa. */
              onPointerDown={(event) => {
                if (lleno) return;
                onPickUp(furniture, event);
              }}
            >
              <span
                className="iglu-ficha__figura"
                style={{ background: colorDe(furniture) }}
                aria-hidden="true"
              />
              <span className="iglu-ficha__cuenta">{disponibles(furniture.id)}</span>
              <span className="iglu-ficha__nombre">{furniture.name}</span>
            </button>
          ))}
        </div>

        <div className="iglu-barra__herramientas">
          {/* El saldo vive acá porque el carnet —que normalmente lo lleva— se esconde
              mientras se decora: la barra ocupa su sitio. */}
          <span className="iglu-barra__saldo">
            {caja ? "CAJA " : ""}◉ {balance}
          </span>
          <span className="iglu-barra__cuenta">
            {snapshot.placements.length} / {snapshot.maxPlaced}
          </span>

          <button
            className={`iglu-tool${grid ? " iglu-tool--on" : ""}`}
            type="button"
            onClick={onToggleGrid}
            title="Enseña dónde acaba el piso"
          >
            REJILLA
          </button>

          <button className="iglu-tool iglu-tool--listo" type="button" onClick={onDone}>
            LISTO
          </button>
        </div>
      </div>

      {/*
        Los tres botones de la derecha, como en el editor de Club Penguin. Cada uno es un
        sitio distinto: lo que hay en el cuarto, lo que se compra y lo que se guarda.
      */}
      <div className="iglu-lado">
        <button
          className="iglu-chapa"
          type="button"
          onClick={onOpenStyle}
          title="Cambiar el piso y la pared"
        >
          <IconIglu />
          <span className="iglu-chapa__label">{caja ? "SALA" : "IGLÚ"}</span>
        </button>

        <button className="iglu-chapa" type="button" onClick={onOpenShop} title="Comprar muebles">
          <IconTienda />
          <span className="iglu-chapa__label">TIENDA</span>
        </button>

        {/*
          El basurero de Club Penguin, con su nombre honesto: acá no se destruye nada.
          Soltar un mueble encima lo devuelve a la barra, y lo que pagaste sigue siendo
          tuyo. `data-iglu-soltar` es cómo lo reconoce el arrastre: el mueble se suelta
          sobre DOM, no sobre el canvas, así que se resuelve con `elementFromPoint`.
        */}
        <div
          className={`iglu-chapa iglu-chapa--caja${dragging ? " iglu-chapa--activa" : ""}`}
          data-iglu-soltar="guardar"
          title="Suelta un mueble aquí para guardarlo"
        >
          <IconCaja />
          <span className="iglu-chapa__label">GUARDAR</span>
        </div>
      </div>
    </>
  );
}

/**
 * El color de una pieza mientras no tenga arte.
 *
 * Sale de su tipo, no de una lista aparte: así un mueble nuevo se ve distinto de los
 * otros el día que se agrega al catálogo, sin tocar esto.
 */
function colorDe(furniture: FurnitureDef): string {
  if (furniture.kind === "silla") return "#c0714f";
  if (furniture.kind === "mesa") return "#a9723f";
  return "#4c9c5b";
}
